import React, { useState, useEffect } from "react";
import { PaperClipIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { CreateRequestInput } from "../../../types/explanations";
import { useReferenceData } from "../../../hooks/useExplanationApi";
import { GetStudentClassesOutput } from "../../../server/Explanations CRUD/OutputsTypes";

interface RequestFormProps {
  onSubmit: (data: CreateRequestInput) => void;
}

interface FormData {
  schoolClass: string;
  studentEmail: string;
  comment: string;
  availability: string;
  files: File[];
}

interface FormErrors {
  schoolClass?: string;
  studentEmail?: string;
  comment?: string;
  availability?: string;
  files?: string;
}

const AVAILABILITY_MAX_LENGTH = 500;

const RequestForm: React.FC<RequestFormProps> = ({ onSubmit }) => {
  const [formData, setFormData] = useState<FormData>({
    schoolClass: "",
    studentEmail: "",
    comment: "",
    availability: "",
    files: [],
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [classes, setClasses] = useState<{ _id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  // Utiliser le hook pour les données de référence
  const { getStudentClasses } = useReferenceData();

  // Charger les données de référence
  useEffect(() => {
    const fetchData = async () => {
      try {
        const classesResponse = await getStudentClasses();

        if (classesResponse.status === 200 && classesResponse.body) {
          const classesData = classesResponse.body as GetStudentClassesOutput;
          setClasses(classesData.classes);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getStudentClasses]);

  // Validation en temps réel
  const validateField = (
    name: keyof FormData,
    value: any,
  ): string | undefined => {
    switch (name) {
      case "schoolClass":
        return !value ? "La classe est obligatoire" : undefined;
      case "studentEmail":
        if (!value) return "L'email est obligatoire";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value) ? "Format d'email invalide" : undefined;
      case "comment":
        if (!value) return "Le commentaire est obligatoire";
        return value.length < 50
          ? "Le commentaire doit contenir au moins 50 caractères"
          : undefined;
      case "availability":
        return value && value.length > AVAILABILITY_MAX_LENGTH
          ? `Maximum ${AVAILABILITY_MAX_LENGTH} caractères`
          : undefined;
      case "files":
        const files = value as File[];
        for (const file of files) {
          if (file.size > 10 * 1024 * 1024) {
            return "Chaque fichier doit faire moins de 10MB";
          }
          const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
          if (!allowedTypes.includes(file.type)) {
            return "Seuls les fichiers JPG, PNG et PDF sont acceptés";
          }
        }
        return undefined;
      default:
        return undefined;
    }
  };

  // Gestion des changements de champs
  const handleFieldChange = (name: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validation en temps réel
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Gestion de l'upload de fichiers
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const newFiles = [...formData.files, ...files];

    // Vérifier la limite de fichiers
    if (newFiles.length > 5) {
      setErrors((prev) => ({ ...prev, files: "Maximum 5 fichiers autorisés" }));
      return;
    }

    handleFieldChange("files", newFiles);
  };

  // Supprimer un fichier
  const removeFile = (index: number) => {
    const newFiles = formData.files.filter((_, i) => i !== index);
    handleFieldChange("files", newFiles);
  };

  // Validation complète du formulaire
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    Object.keys(formData).forEach((key) => {
      const fieldName = key as keyof FormData;
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);

      // Réinitialiser le formulaire
      setFormData({
        schoolClass: "",
        studentEmail: "",
        comment: "",
        availability: "",
        files: [],
      });
      setErrors({});
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Vérifier si le formulaire est valide
  const isFormValid =
    Object.values(errors).every((error) => !error) &&
    formData.schoolClass &&
    formData.studentEmail &&
    formData.comment.length >= 50;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Demander une explication
        </h2>
        <p className="text-sm sm:text-base text-gray-600">
          Remplissez ce formulaire pour demander une explication personnalisée
          de vos exercices
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Classe */}
        <div>
          <label
            htmlFor="schoolClass"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Classe <span className="text-red-500">*</span>
          </label>
          <select
            id="schoolClass"
            value={formData.schoolClass}
            onChange={(e) => handleFieldChange("schoolClass", e.target.value)}
            className={`w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.schoolClass ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Sélectionnez votre classe</option>
            <option value="terminale_A1">Terminale A1</option>
            <option value="terminale_A2">Terminale A2</option>
            <option value="terminale_B">Terminale B</option>
            <option value="terminale_C">Terminale C</option>
            <option value="terminale_D">Terminale D</option>
            <option value="terminale_G1">Terminale G1</option>
            <option value="terminale_G2">Terminale G2</option>
            <option value="terminale_G3">Terminale G3</option>
            <option value="troisieme_mc">Troisième MC</option>
            <option value="troisieme_ml">Troisième ML</option>
          </select>
          {errors.schoolClass && (
            <p className="mt-1 text-sm text-red-600">{errors.schoolClass}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label
            htmlFor="studentEmail"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="studentEmail"
            value={formData.studentEmail}
            onChange={(e) => handleFieldChange("studentEmail", e.target.value)}
            className={`w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.studentEmail ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="votre.email@exemple.com"
          />
          {errors.studentEmail && (
            <p className="mt-1 text-sm text-red-600">{errors.studentEmail}</p>
          )}
        </div>

        {/* Commentaire/Description */}
        <div>
          <label
            htmlFor="comment"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Décrivez votre problème <span className="text-red-500">*</span>
          </label>
          <textarea
            id="comment"
            value={formData.comment}
            onChange={(e) => handleFieldChange("comment", e.target.value)}
            rows={4}
            className={`w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.comment ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Décrivez en détail le problème que vous rencontrez avec cet exercice..."
          />
          <div className="mt-1 flex justify-between text-sm">
            {errors.comment ? (
              <p className="text-red-600">{errors.comment}</p>
            ) : (
              <p className="text-gray-500">Minimum 50 caractères</p>
            )}
            <p
              className={`${
                formData.comment.length < 50 ? "text-red-500" : "text-green-600"
              }`}
            >
              {formData.comment.length}/50
            </p>
          </div>
        </div>

        {/* Disponibilités */}
        <div>
          <label
            htmlFor="availability"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Vos disponibilités (optionnel)
          </label>
          <textarea
            id="availability"
            value={formData.availability}
            onChange={(e) => handleFieldChange("availability", e.target.value)}
            rows={2}
            maxLength={AVAILABILITY_MAX_LENGTH}
            className={`w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.availability ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Ex : mardi et jeudi après 17h, samedi matin"
          />
          <div className="mt-1 flex justify-between text-sm">
            {errors.availability ? (
              <p className="text-red-600">{errors.availability}</p>
            ) : (
              <p className="text-gray-500">
                Aide l'enseignant à vous proposer des créneaux adaptés
              </p>
            )}
            <p className="text-gray-500">
              {formData.availability.length}/{AVAILABILITY_MAX_LENGTH}
            </p>
          </div>
        </div>

        {/* Upload de fichiers */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Fichiers joints (optionnel)
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 text-center hover:border-gray-400 transition-colors">
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer flex flex-col items-center"
            >
              <PaperClipIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 mb-2" />
              <span className="text-xs sm:text-sm text-gray-600">
                <span className="hidden sm:inline">
                  Glissez-déposez vos fichiers ici ou{" "}
                </span>
                <span className="text-blue-600 hover:text-blue-500">
                  cliquez pour parcourir
                </span>
              </span>
              <span className="text-xs text-gray-500 mt-1">
                JPG, PNG, PDF - Max 10MB
              </span>
            </label>
          </div>

          {/* Liste des fichiers uploadés */}
          {formData.files.length > 0 && (
            <div className="mt-4 space-y-2">
              {formData.files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-gray-50 p-2 sm:p-3 rounded-lg"
                >
                  <div className="flex items-center min-w-0 flex-1">
                    <PaperClipIcon className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-700 truncate">
                      {file.name}
                    </span>
                    <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {errors.files && (
            <p className="mt-1 text-sm text-red-600">{errors.files}</p>
          )}
        </div>

        {/* Bouton de soumission */}
        <div className="pt-2 sm:pt-4">
          <button
            type="submit"
            disabled={!isFormValid || isSubmitting}
            className={`w-full py-2.5 sm:py-3 px-4 rounded-lg font-medium text-sm sm:text-base transition-colors ${
              isFormValid && !isSubmitting
                ? "bg-[#E3B500] hover:bg-[#CA8A04] text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                <span className="text-sm sm:text-base">Envoi en cours...</span>
              </div>
            ) : (
              "Envoyer ma demande"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RequestForm;
